import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Paper, Button, useTheme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useNavigate } from 'react-router-dom';
import { EmailEditor } from '../components/EmailEditor';
import { PreviewPanel } from '../components/PreviewPanel';
import { DeviceControls, DeviceSize, DEVICE_PRESETS } from '../components/DeviceControls';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import { TemplateManager } from '../components/TemplateManager';
import { EmailAnalyzer } from '../components/EmailAnalyzer';
import { LoginDialog } from '../components/LoginDialog';
import { useAuth } from '../contexts/AuthContext';
import { checkProStatus } from '../services/stripe';
import { EmailExporter } from '../components/EmailExporter';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export const EmailPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [html, setHtml] = useState('');
  const [clientType, setClientType] = useState<'gmail' | 'outlook' | 'apple' | 'yahoo' | 'thunderbird' | 'plaintext'>('gmail');
  const [isPro, setIsPro] = useState(false);
  const [currentDevice, setCurrentDevice] = useState('mobile');
  const [previewSize, setPreviewSize] = useState<DeviceSize>(DEVICE_PRESETS.mobile);
  const { user, signOut } = useAuth();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const checkStatus = async () => {
      if (user) {
        const status = await checkProStatus();
        setIsPro(status);
      }
    };
    checkStatus();
  }, [user]);

  const handleDeviceChange = (size: DeviceSize) => {
    setCurrentDevice(size.name.toLowerCase());
    setPreviewSize(size);
  };

  const handleCustomSizeChange = (width: number, height: number) => {
    setCurrentDevice('custom');
    setPreviewSize({ width, height, name: 'Custom' });
  };

  const handleClientChange = (client: 'gmail' | 'outlook' | 'apple' | 'yahoo' | 'thunderbird' | 'plaintext') => {
    setClientType(client);
  };

  const handleUpgradeClick = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    // For testing, immediately set isPro to true
    setIsPro(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return <LoginDialog open={true} onClose={() => navigate('/')} />;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8FAFF 0%, #E8EEFF 100%)',
      pb: 8
    }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #2A2F6F 0%, #0F1642 100%)',
        color: 'white',
        py: { xs: 2, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 150%, rgba(42, 47, 111, 0.7) 0%, rgba(15, 22, 66, 0) 50%)',
          zIndex: 1
        }
      }}>
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }
          }}>
            <Box>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2.125rem' },
                  background: 'linear-gradient(90deg, #FFFFFF 0%, #E0E7FF 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: { xs: 0.5, sm: 1 }
                }}
              >
                Mailzen
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#E0E7FF',
                  opacity: 0.9,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Design and test your email templates across different clients and devices
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1, sm: 2 },
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              p: { xs: 1.5, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Typography sx={{ 
                color: '#E0E7FF',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                {user.email}
              </Typography>
              <Box sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1
              }}>
                <Button 
                  fullWidth={isMobile}
                  variant="contained" 
                  onClick={handleSignOut}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #5558E6 0%, #7C4DEF 100%)',
                    }
                  }}
                >
                  Sign Out
                </Button>
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => setIsExportDialogOpen(true)}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      background: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Export Previews
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 } }}>
        <SubscriptionBanner isPro={isPro} onUpgradeClick={handleUpgradeClick} />
        
        <Box sx={{ flexGrow: 1, mt: 4 }}>
          <Grid container spacing={{ xs: 2, sm: 4 }}>
            <Grid xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: { xs: '12px', sm: '16px' },
                  background: 'white',
                  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.1)',
                  border: '1px solid rgba(99, 102, 241, 0.1)'
                }}
              >
                <DeviceControls
                  isPro={isPro}
                  onDeviceChange={handleDeviceChange}
                  onCustomSizeChange={handleCustomSizeChange}
                  currentDevice={currentDevice}
                />
              </Paper>
            </Grid>
            <Grid xs={12} md={6}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2, sm: 4 },
                height: '100%'
              }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 2, sm: 3 },
                    borderRadius: { xs: '12px', sm: '16px' },
                    background: 'white',
                    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.1)'
                  }}
                >
                  <TemplateManager
                    currentHtml={html}
                    onTemplateSelect={setHtml}
                    isPro={isPro}
                    onUpgradeClick={handleUpgradeClick}
                  />
                </Paper>

                <EmailAnalyzer html={html} isPro={isPro} />

                <Box sx={{ flex: 1, minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#2A2F6F',
                      fontWeight: 600,
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                      mb: { xs: 0.5, sm: 1 }
                    }}
                  >
                    HTML Editor
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#6B7280',
                      fontSize: { xs: '0.813rem', sm: '0.875rem' },
                      mb: { xs: 1.5, sm: 2 }
                    }}
                  >
                    Write or paste your email HTML here
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <EmailEditor onChange={setHtml} />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid xs={12} md={6}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#2A2F6F',
                    fontWeight: 600,
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    mb: { xs: 0.5, sm: 1 }
                  }}
                >
                  Preview
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: { xs: '0.813rem', sm: '0.875rem' },
                    mb: { xs: 1.5, sm: 2 }
                  }}
                >
                  See how your email looks in different clients
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <PreviewPanel 
                    html={html} 
                    clientType={clientType}
                    isPro={isPro}
                    onClientChange={handleClientChange}
                    deviceSize={previewSize}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <EmailExporter
        html={html}
        open={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        isPro={isPro}
      />
    </Box>
  );
}; 